import { Typography } from "@mui/material";
import { useState } from "react";
import Box from "../../../design/components-dev/BoxExtended";
import { ServiceProviderType } from "../../../profile-sdk";
import { API_USERS_BACKEND } from "../../../api/api";
import { useXNGSelector } from "../../../context/store";
import { selectUser } from "../../../context/slices/userProfileSlice";
import { selectStateInUS } from "../../../context/slices/stateInUsSlice";
import useApiQueryGetServiceProviderTypesByDate from "../../../api/hooks/state_snapshots/use_api_query_get_service_provider_types_by_date";
import { MSBInputErrorWrapper, MSBSearchMultiselect } from "../../../fortitude";
import msbMUIAutoCompleteFilterOptions from "../../../utils/msb_mui_auto_complete_filter_options";
import XNGButton from "../../../design/low-level/button";
import { getSizing } from "../../../design/sizing";
import { BLUE_BACKGROUND, FloatingLayout, HEADER_SIZE } from "../layout";

export default function ServiceProviderTypeRequiredView() {
  const user = useXNGSelector(selectUser);
  const stateInUS = useXNGSelector(selectStateInUS);

  const [selectedTypes, setSelectedTypes] = useState<ServiceProviderType[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: serviceProviderTypesOptionsResponse,
    isError: optionsIsError,
    isPending: optionsIsPending,
    refetch: refetchOptions,
  } = useApiQueryGetServiceProviderTypesByDate({
    queryParams: { state: stateInUS },
  });

  const handleSave = async () => {
    if (!user?.id || selectedTypes.length === 0) return;
    setIsSaving(true);
    try {
      await Promise.all(
        selectedTypes
          .filter((t) => t.id != null)
          .map((t) =>
            API_USERS_BACKEND.postProviderType({
              id: user.id!,
              postProviderTypeBody: { serviceProviderType: Number(t.id) },
            }),
          ),
      );
      window.location.reload();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={BLUE_BACKGROUND}>
      <FloatingLayout>
        <Typography variant={HEADER_SIZE}>Service Provider Type Required</Typography>
        <Typography sx={{ marginTop: getSizing(2), textAlign: "justify" }} variant="body1">
          To ensure your sessions are documented correctly, please select your Service Provider
          Type. This is a one time step required to continue using X Logs and should only take a
          moment to complete.
        </Typography>
        <Box sx={{ marginTop: getSizing(4) }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, marginBottom: getSizing(1) }}>
            Select Service Provider Type
          </Typography>
          <MSBInputErrorWrapper
            isError={optionsIsError}
            displayRefreshButton={optionsIsError}
            errorText={
              optionsIsError
                ? "Failed to load Service Provider Types, please click refresh icon to retry"
                : ""
            }
            refetch={refetchOptions}
          >
            <MSBSearchMultiselect
              selectedOptions={selectedTypes}
              options={serviceProviderTypesOptionsResponse?.serviceProviderTypes || []}
              getOptionLabel={(option) => `${option?.name}`}
              onChange={(newTypes) => setSelectedTypes(newTypes)}
              renderOptionVariant="checkbox"
              variant="no overflow after 1"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              label={(() => {
                if (optionsIsPending) return "Loading Service Provider Types Options...";
                if (optionsIsError) return "Failed to load Service Provider Types Options";
                return "Service Provider Type(s)";
              })()}
              disabled={optionsIsPending || optionsIsError}
              autocompleteProps={{
                disableCloseOnSelect: true,
                filterOptions: msbMUIAutoCompleteFilterOptions(),
                fullWidth: true,
              }}
            />
          </MSBInputErrorWrapper>
        </Box>
        <Box
          sx={{
            paddingTop: getSizing(3),
            display: "flex",
            justifyContent: "center",
          }}
        >
          <XNGButton onClick={handleSave} disabled={selectedTypes.length === 0 || isSaving}>
            Save
          </XNGButton>
        </Box>
      </FloatingLayout>
    </Box>
  );
}
